// External Imports
import { useState, useContext, useEffect } from "react";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useLocation } from "react-router-dom";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import Radio from "../UI/Radio";
import BookingCard from "./BookingCard";
import SearchBar from "../UI/SearchBar";
import Heading from "../UI/Heading";
import DateFilter from "../UI/DateFilter";
import PaginationMenu from "../UI/PaginationMenu";
import FilterMenu from "../UI/FilterMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";

/** ========================================================================
 * * Bookings Page
 * Renders a page, which displays all bookings.
 * ========================================================================== */

export default function BookingsPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    status: "active",
    limit: 50,
    scope: "All",
    query: "",
    date: "dateCreated",
    invoices: "All",
    extension: "All",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "booking",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/bookings?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setBookings(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Date",
      options: [
        { label: "Date Created", value: "dateCreated" },
        { label: "End Date", value: "endDate" },
        { label: "Start Date", value: "startDate" },
      ],
      selected: filter.date,
    },
    {
      label: "Invoices",
      options: [
        { label: "All", value: "All" },
        { label: "Paid", value: "paid" },
        { label: "Unpaid", value: "unpaid" },
      ],
      selected: filter.invoices,
    },
    {
      label: "Extension",
      options: [
        { label: "All", value: "All" },
        { label: "Past Due", value: "due" },
        { label: "Not Due", value: "not-due" },
      ],
      selected: filter.extension,
    },
  ];

  // Fetch data on load and when filter changes
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading title="Bookings">
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <DomainSwitch
          on={filter.scope !== "All"}
          onSwitch={(scope) => setFilter({ ...filter, scope })}
        />
        <MdAdd
          onClick={(e) => navigate("/bookings/add", e)}
          className="text-3xl icon"
          title="Add Booking"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={bookings} filename="bookings" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      <div className="grid grid-cols-4 gap-4 h-fit">
        <Radio
          selected={filter.status}
          options={[
            "Active",
            { id: "confirmed", name: "Upcoming" },
            "Completed",
            "Cancelled",
          ]}
          onClick={(status) =>
            setFilter({ ...filter, status: status.toLowerCase() })
          }
        />
      </div>

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : bookings.length > 0 ? (
        <div className="list">
          {bookings.map((booking) => (
            <BookingCard booking={booking} key={booking.id} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={bookings.length}
      />
    </div>
  );
}
